/**
 * Centralized Filter System Constants
 * 
 * Single source of truth for all filter-related:
 * - SQL operators
 * - Italian label mappings
 * - Filter types and value types
 * - Helper functions for consistent behavior
 */

// =============================
// Core Filter Types
// =============================

/** Available filter types */
export const FILTER_TYPES = ['simple','select','ambito','categories','eroganti'] as const;
export type FilterType = typeof FILTER_TYPES[number];

/** Available filter value types */
export const FILTER_VALUE_TYPES = ['string', 'number', 'boolean', 'date', 'datetime', 'jsonb', 'array'] as const;
export type FilterValueType = typeof FILTER_VALUE_TYPES[number];

/** Available render types for columns */
export const RENDER_TYPES = ['text', 'number', 'date', 'boolean', 'custom'] as const;
export type RenderType = typeof RENDER_TYPES[number];

export interface FilterSelectSettings {
    keyname?: string; // se il filtro è di tipo select, il nome della chiave per l'etichetta se filterSelectSource è un oggetto (default: 'id')
    valuename?: string; // se il filtro è di tipo select, il nome della chiave per il valore se filterSelectSource è un oggetto (default: 'value')
}
// =============================
// SQL Operators by Filter Type
// =============================

/** SQL operators grouped by filter type */
export const FILTER_OPERATORS = {
    simple: [
        'ILIKE',      // Contains (case-insensitive)
        '=',          // Equals
        '!=',         // Not equals
        '>',          // Greater than
        '<',          // Less than
        '>=',         // Greater or equal
        '<=',         // Less or equal
        'BETWEEN',    // Between two values (inclusive)
        'IS NULL',    // Is empty
        'IS NOT NULL' // Is not empty
    ],
    jsonb: [
        '@>',                  // Contains JSON object/array
        '?',                   // Has key
        '?&',                  // Has all keys
        '?|',                  // Has any keys
        '@@',                  // JSON path query
        '#>',                  // Get JSON object at path
        '#>>',                 // Get JSON object at path as text
        '->',                  // Get JSON object field
        '->>',                 // Get JSON object field as text
        'jsonb_path_exists'    // JSON path exists
    ],
    array: [
        'ANY',        // Any element matches
        'ALL',        // All elements match
        'OVERLAP'     // Arrays overlap
    ],
    select:[
        '='
    ],
    ambito:[
        '='
    ],
    categories:[
        '='
    ],
    eroganti:[
        '='
    ]
} as const;

// Union type of all possible operators
export type FilterOperator = 
    | typeof FILTER_OPERATORS.simple[number]
    | typeof FILTER_OPERATORS.jsonb[number]
    | typeof FILTER_OPERATORS.array[number]
    | typeof FILTER_OPERATORS.select[number]
    | typeof FILTER_OPERATORS.ambito[number]
    | typeof FILTER_OPERATORS.categories[number];

// =============================
// Italian Operator Labels
// =============================

/** Complete Italian labels for all SQL operators */
export const OPERATOR_LABELS = {
    // Simple operators
    'ILIKE': 'Contiene',
    '=': 'Uguale a',
    '!=': 'Diverso da',
    '>': 'Maggiore di',
    '<': 'Minore di',
    '>=': 'Maggiore o uguale a',
    '<=': 'Minore o uguale a',
    'IS NULL': 'È vuoto',
    'IS NOT NULL': 'Ha valore',
    
    // JSONB operators
    '@>': 'Contiene JSON',
    '?': 'Ha chiave',
    '?&': 'Ha tutte le chiavi',
    '?|': 'Ha qualche chiave',
    '@@': 'Query JSON path',
    '#>': 'Ottieni oggetto JSON',
    '#>>': 'Ottieni oggetto JSON come testo',
    '->': 'Ottieni campo JSON',
    '->>': 'Ottieni campo JSON come testo',
    'jsonb_path_exists': 'Percorso JSON esiste',
    
    // Array operators
    'ANY': 'Qualsiasi di',
    'ALL': 'Tutti di',
    'OVERLAP': 'Si sovrappone',
    
    // Range operators
    'BETWEEN': 'Tra'
} as const;

// =============================
// Helper Functions
// =============================

/**
 * Get available operators for a specific filter type
 */
export function getOperatorsForFilterType(filterType: FilterType): readonly string[] {
    return FILTER_OPERATORS[filterType];
}

/**
 * Get Italian label for an SQL operator
 * @param operator SQL operator
 * @returns Italian label or original operator if not found
 */
export function getOperatorLabel(operator: string): string {
    return OPERATOR_LABELS[operator as FilterOperator] || operator;
}

export function getOperatorsList(operators: readonly string[]): Record<FilterOperator, string> {
    return operators.reduce((acc, op) => {
        acc[op as FilterOperator] = getOperatorLabel(op);
        return acc;
    }, {} as Record<FilterOperator, string>);

}
/**
 * Check if an operator is valid for a filter type
 */
export function isOperatorValidForFilterType(operator: string, filterType: FilterType): boolean {
    return (FILTER_OPERATORS[filterType] as readonly string[]).includes(operator);
}

/**
 * Get all available operators (flattened)
 */
export function getAllOperators(): string[] {
    return [
        ...FILTER_OPERATORS.simple,
        ...FILTER_OPERATORS.jsonb,
        ...FILTER_OPERATORS.array,
        ...FILTER_OPERATORS.select, // inutile ma lo metto per completezza
        ...FILTER_OPERATORS.ambito, // inutile ma lo metto per completezza
        ...FILTER_OPERATORS.categories, // inutile ma lo metto per completezza
    ];
}

/**
 * Get operators that require a value input
 * (excludes IS NULL, IS NOT NULL)
 */
export function getOperatorsThatRequireValue(): string[] {
    const allOps = getAllOperators();
    const noValueOps = ['IS NULL', 'IS NOT NULL'];
    return allOps.filter(op => !noValueOps.includes(op));
}

// =============================
// Default Operators by Value Type
// =============================

/** Default operators for each filter value type */
export const DEFAULT_OPERATORS_BY_VALUE_TYPE = {
    string: ['ILIKE', '=', '!=', 'IS NULL', 'IS NOT NULL'],
    number: ['=', '>', '<', '>=', '<=', '!=', 'IS NULL', 'IS NOT NULL'],
    boolean: ['=', '!=', 'IS NULL', 'IS NOT NULL'],
    date: ['=', '>', '<', '>=', '<=', '!=', 'IS NULL', 'IS NOT NULL','BETWEEN'],
    datetime: ['=', '>', '<', '>=', '<=', '!=', 'IS NULL', 'IS NOT NULL','BETWEEN'],
    jsonb: ['@>', '?', '?&', '?|', 'jsonb_path_exists'],
    array: ['ANY', 'ALL', 'OVERLAP']
} as const;

/**
 * Get default operators for a value type
 */
export function getDefaultOperatorsForValueType(valueType: FilterValueType): readonly string[] {
    return DEFAULT_OPERATORS_BY_VALUE_TYPE[valueType];
}